import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack, TablePagination } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid';
import {
  gridPageSelector,
  gridRowSelectionStateSelector,
  GridSelectedRowCount,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';

import { styles } from './index.styles';

export interface CustomFooterProps {
  isFullSelection: boolean;
  onSelectAll: () => void;
  rowCount: number; // Pass rowCount manually as the `useGridSelector`-hook does not provide a reliable value.
  rowsPerPage: number;
}

export const UserListFooter: FC<CustomFooterProps> = ({
  isFullSelection,
  onSelectAll,
  rowCount,
  rowsPerPage,
}) => {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const selectionModel = useGridSelector(
    apiRef,
    gridRowSelectionStateSelector,
  ) as GridRowSelectionModel;
  const selectedRowCount = selectionModel?.ids?.size ?? 0;
  const disableSelectAll = isFullSelection || selectedRowCount === rowCount;

  return (
    <Stack className="UserListFooter-root" sx={styles.userListFooter}>
      <Stack direction="row">
        <GridSelectedRowCount
          selectedRowCount={isFullSelection ? rowCount : selectedRowCount}
        />

        <Button
          data-test-id="user-admin-select-all-button"
          disabled={disableSelectAll}
          onClick={() => onSelectAll()}
          variant="text"
        >
          {t('admin:List.SelectAll')}
        </Button>
      </Stack>

      <TablePagination
        count={rowCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
        onPageChange={(event, value) => apiRef.current.setPage(value)}
        component="div"
      />
    </Stack>
  );
};
